<template>
  <div class="detail-root">
    <!-- 顶栏：返回 + 账号 -->
    <div class="top-bar">
      <span class="back" @click="goBack">↩</span>
      <div class="account-badge">
        <span class="avatar">{{ userName.charAt(0) }}</span>
        <div class="name-line">
          <span class="name">{{ userName }}</span>
          <span class="nick" v-if="nickName">{{ nickName }}</span>
        </div>
      </div>
      <div class="top-actions">
        <span class="range-btn" :class="{ on: range === 'today' }" @click="setRange('today')">今</span>
        <span class="range-btn" :class="{ on: range === 'yesterday' }" @click="setRange('yesterday')">昨</span>
        <span class="range-btn" :class="{ on: range === '2d' }" @click="setRange('2d')">2d</span>
        <span class="range-btn" :class="{ on: range === '3d' }" @click="setRange('3d')">3d</span>
        <span class="range-btn" :class="{ on: range === '7d' }" @click="setRange('7d')">7d</span>
      </div>
    </div>

    <!-- 实时状态 -->
    <div class="status-strip">
      <div class="ss-item">
        <span class="ss-label">状态</span>
        <span class="ss-val" :class="online ? 'on' : 'off'">{{ online ? '直播中' : '离线' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">所在房间</span>
        <span class="ss-val">{{ resolveRoomName(room) || '未进房' }}</span>
      </div>
      <!-- 在线只显示上线时间，离线只显示最后离线时间（两者不会同时展示） -->
      <div class="ss-item">
        <span class="ss-label">{{ online ? '上线时间' : '最后离线' }}</span>
        <span class="ss-val">{{
          online
            ? (onlineTime ? fmtTime(onlineTime) : '—')
            : (lastOfflineTime ? fmtTime(lastOfflineTime) : '—')
        }}</span>
      </div>
    </div>

    <!-- 直播统计：单行紧凑（标签+数值内联，不再占两行大卡片） -->
    <div class="stats-bar" v-if="debugMode">
      <div class="stat-card">
        <span class="sc-label">直播场次</span>
        <span class="sc-val">{{ totalCount }}</span>
      </div>
      <div class="stat-card" :class="netWinCardClass">
        <span class="sc-label">直播收入</span>
        <span class="sc-val" :class="netWinClass">{{ totalNetWinStr }}</span>
      </div>
    </div>

    <!-- 内容区：撑满剩余空间，内部滚动 -->
    <div class="content-wrap">
      <div class="scroll-area">
        <div v-if="loading" class="loading">正在拉取直播数据...</div>
        <div v-else-if="records.length === 0" class="empty">暂无直播记录</div>

        <!-- 记录网格：手机 1 列；PC 宽屏 2~3 列（卡片保持紧凑单列布局） -->
        <div class="record-grid">
          <div
            class="record"
            v-for="r in records"
            :key="r.order_number || r.id"
            :class="[recordBoxClass(r), { unfinished: !r.is_finish, expanded: killOpen(r), clickable: r.kill_flag > 0 }]"
            @click="onRecordClick(r)"
          >
          <!-- 头部：直播间信息 -->
          <div class="rec-top">
            <div class="rec-top-row1">
              <span class="live-dot" v-if="!r.is_finish"></span>
              <!-- 只显示房间号（EU1/EU5）+ 房间编号（3001-57），游戏名不占地方 -->
              <span class="rec-desk">
                {{ roomDisplayName(r) }}<i v-if="roomNo(r)" class="rec-room-no">{{ roomNo(r) }}</i>
              </span>
              <!-- 直播中不再重复标注（绿点已是直播信号、蓝角标已是待开奖），只标「已结束」 -->
              <span v-if="r.is_finish" class="rec-status done">已结束</span>
              <!-- 杀猪结论：跳=红标(跳转锚点)、然=绿标(自然死亡)、略=琥珀标(条件不符等跳过)。
                   桌面悬停有 title；查看完整原因=点整条卡片（模式B，手机上点击区域最大），再点收起；
                   同时只展开一条（手风琴），结算推送到达时自动展开。标签带 ▾ 角标，展开后旋转 -->
              <span
                v-if="r.kill_flag === 2"
                class="rec-kill-tag kill"
                :class="{ open: killOpen(r) }"
                :title="`跳转：落在「${r.kill_desc}」的锚点局`"
                @click.stop="toggleKill(r)"
              >跳<i class="k-arrow">▾</i></span>
              <span
                v-else-if="r.kill_flag === 1"
                class="rec-kill-tag natural"
                :class="{ open: killOpen(r) }"
                :title="r.kill_desc"
                @click.stop="toggleKill(r)"
              >然<i class="k-arrow">▾</i></span>
              <span
                v-else-if="r.kill_flag === 3"
                class="rec-kill-tag noskill"
                :class="{ open: killOpen(r) }"
                :title="r.kill_desc"
                @click.stop="toggleKill(r)"
              >略<i class="k-arrow">▾</i></span>
              <span class="rec-result-chip" v-if="debugMode && betLabel(r)" :class="betColorClass(r)">
                {{ betLabel(r) }}<i v-if="r.odds"> · {{ r.odds }}</i>
              </span>
            </div>
            <!-- 点卡片展开的完整原因（手风琴：同时只展开一条；结算推送到达自动展开） -->
            <div v-if="killOpen(r)" class="rec-kill-desc">{{ killFullText(r) }}</div>
          </div>

          <!-- PK 对战区 - 真实模式：传统扑克牌 -->
          <div class="pk-real-showdown" v-if="debugMode && (r.xian_cards || r.zhuang_cards)">
            <!-- 信息行 -->
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <!-- 对决行 -->
            <div class="showdown-row">
              <!-- 我方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.xian_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
              <!-- VS 比分 -->
              <div class="showdown-vs">
                <span class="vs-score-xian" v-if="r.xian_dian != null">{{ r.xian_dian }}</span>
                <span class="vs-word">vs</span>
                <span class="vs-score-zhuang" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}</span>
              </div>
              <!-- 对方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.zhuang_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
            </div>
          </div>

          <!-- PK 对战区 - 礼物模式：图标徽章 -->
          <div class="pk-zone" v-else-if="!debugMode && (r.xian_cards || r.zhuang_cards)">
            <div class="pk-row">
              <span class="pk-tag blue">我方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.xian_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.xian_dian != null">{{ r.xian_dian }}<i>分</i></span>
            </div>
            <div class="pk-divider"><span>VS</span></div>
            <div class="pk-row">
              <span class="pk-tag red">对方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.zhuang_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}<i>分</i></span>
            </div>
          </div>

          <!-- 无牌面（正在下注未开牌 / 历史无牌记录）：占位区，带上信息行与等待态，避免卡片空旷 -->
          <div class="pk-live" v-else>
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}-{{ r.sub_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <div class="live-wait">
              <i class="card-back"></i>
              <i class="card-back"></i>
              <span class="live-wait-txt">{{ r.is_finish ? '本局无牌面记录' : '等待开牌…' }}</span>
              <i class="card-back"></i>
              <i class="card-back"></i>
            </div>
          </div>

          <!-- 收益明细 -->
          <div class="rec-amounts">
            <div class="am">
              <span class="am-icon">投</span>
              <span class="am-val gold">{{ Tools.formatMoney(r.total_betting_amount) }}</span>
            </div>
            <!-- 有效投注：仅 PC 宽屏显示（手机保持原来的三项，不挤） -->
            <div class="am am-pc-only">
              <span class="am-icon">效</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.effective_betting_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">赢</span>
              <span class="am-val" :class="winColor(r.real_win_total_amount)">{{ Tools.formatMoney(r.real_win_total_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">额</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.front_balance) }} → {{ Tools.formatMoney(r.after_balance) }}</span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- 底部分页：始终在底部 -->
      <div class="pager-bar">
        <span class="pg-btn" :class="{ off: currentPage <= 1 }" @click="goPage(currentPage - 1)">‹</span>
        <span class="pg-num" v-if="totalPages > 0">{{ currentPage }} / {{ totalPages }} 页</span>
        <span class="pg-num" v-else>暂无数据</span>
        <span class="pg-btn" :class="{ off: currentPage >= totalPages }" @click="goPage(currentPage + 1)">›</span>
      </div>
    </div>

    <!-- 悬浮刷新 -->
    <span class="float-refresh" @click="loadData()">↻</span>

    <!-- 全局控制（总控）：杀开关 + 参与门槛 + 锚点状态，三者合并进同一个悬浮窗 -->
    <QuickKillPanel
      :enabled="qkEnabled"
      :amount="qkAmount"
      :room-count="qkRoomCount"
      :room="room"
      :room-name="resolveRoomName(room)"
      :on-save="qkSave"
      :on-refresh="qkLoad"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, inject, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Tools from '@/ts/Tools';
import { GAME_TYPE_MAP } from '@/ts/roomName';
import { resolveRoomName, setRoomNameMap } from '@/ts/roomNameStore';
import RoomVO from '@/ts/VO/RoomVO';
import SignalRClient from '@/ts/SignalRClient';
import QuickKillPanel from '@/components/QuickKillPanel.vue';
import { useQuickKill } from '@/ts/useQuickKill';

const route = useRoute();
const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));
const userName = ref(route.params.userName as string || '');
const nickName = ref('');

// ===== 全局控制面板（杀开关 + 参与门槛）=====
// 详情页是独立路由，不经过 HomeView，面板的数据需自行从服务端拉取
const {
  enabled: qkEnabled,
  amount: qkAmount,
  roomCount: qkRoomCount,
  load: qkLoad,
  save: qkSave,
} = useQuickKill();

// 返回时回到主播列表页（首页的“主播列表”标签）
function goBack() {
  router.push({ name: 'home', query: { tab: 'recharge' } });
}

// 实时状态（来自列表接口 / GetAccountBets / 服务端推送）
// 进入详情时先用列表已知的实时状态做种子，避免接口滞后导致误判“离线”
const online = ref(route.query.online === '1');
const onlineTime = ref((route.query.ot as string) || '');
const lastOfflineTime = ref((route.query.lo as string) || '');
const room = ref((route.query.room as string) || '');

const range = ref('today');
const loading = ref(false);
const records = ref<any[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const totalNetWin = ref(0);
const pageSize = 20;

// 推送已处理过的订单号 / 该订单已计入的收入金额（用于去重，重新拉页时清空）
const pushedOrders = new Set<string>();
const pushedWin = new Map<string, number>();

// ===== 伪装/真实卡牌切换（由父组件统一 debugMode 控制） =====
const REAL_SUIT: Record<string, string> = { spade: '♠', heart: '♥', club: '♣', diamond: '♦' };
const REAL_RANK: Record<number, string> = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function realSuitSymbol(suit: string) { return REAL_SUIT[suit] || ''; }
function realRankDisplay(n: number) { return REAL_RANK[n] || String(n); }

const totalNetWinStr = computed(() => {
  const v = totalNetWin.value;
  return Tools.formatMoney(v, { showSign: true });
});
const netWinClass = computed(() => totalNetWin.value > 0 ? 'positive' : totalNetWin.value < 0 ? 'negative' : '');
const netWinCardClass = computed(() => totalNetWin.value > 0 ? 'card-win' : totalNetWin.value < 0 ? 'card-lose' : '');
function setRange(r: string) {
  range.value = r;
  currentPage.value = 1;
  loadData();
}

function goPage(p: number) {
  if (p < 1 || p > totalPages.value) return;
  currentPage.value = p;
  loadData();
}

async function loadData() {
  loading.value = true;
  try {
    const now = new Date();
    let start: string, end: string = toDateStr(now);
    if (range.value === 'today') start = end;
    else if (range.value === 'yesterday') { start = end = toDateStr(new Date(now.getTime() - 86400000)); }
    else if (range.value === '2d') start = toDateStr(new Date(now.getTime() - 86400000));
    else if (range.value === '3d') start = toDateStr(new Date(now.getTime() - 2 * 86400000));
    else if (range.value === '7d') start = toDateStr(new Date(now.getTime() - 6 * 86400000));
    else start = toDateStr(new Date(now.getTime() - 29 * 86400000));

    const resp = await Tools.FetchGetAsync<any>('game/account-bets', {
      userName: userName.value,
      startDate: start,
      endDate: end,
      page: currentPage.value,
      pageSize: pageSize
    });
    if (resp.CheckIsOk() && resp.Data) {
      records.value = resp.Data.data || [];
      // 整页重拉：服务端数据才是准的，清空推送去重账本
      pushedOrders.clear();
      pushedWin.clear();
      totalPages.value = resp.Data.pagecount || 0;
      totalCount.value = resp.Data.allcount || 0;
      // 后端聚合的统计，不再前端拉全量数据计算
      if (resp.Data.stats) {
        totalNetWin.value = resp.Data.stats.total_win || 0;
      }
      if (records.value.length > 0 && records.value[0].nickName) {
        nickName.value = records.value[0].nickName;
      }
      // 实时状态字段（服务端实时计算）
      // online 以“已确认为在线”为准：列表种子或服务端推送若已判在线，接口的滞后 false 不将其降级
      online.value = online.value || !!resp.Data.online;
      onlineTime.value = resp.Data.onlineTime || onlineTime.value;
      lastOfflineTime.value = resp.Data.lastOfflineTime || lastOfflineTime.value;
      room.value = resp.Data.room || room.value;
    }
  } finally {
    loading.value = false;
  }
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ===== 直播间互动玩法映射（见 @/ts/roomName）=====
// 头部只显示房间号（EU1/EU5…）：游戏名（人气对决等）由页面级信息表达，记录头部不再重复占地方
function roomDisplayName(r: any): string {
  return r.game_zhuo_name || r.game_code || '';
}

// 房间编号（如 3001-57）：game_type-game_code，跟在房间号后面用弱化小字展示
function roomNo(r: any): string {
  if (r.game_type && r.game_code) return `${r.game_type}-${r.game_code}`;
  return '';
}

// ===== 直播互动结果判断（仅 debug/真实模式显示） =====

// 杀猪结论小行展开状态（按记录 key 记忆；手机端没悬停，点卡片展开看完整原因）。
// 必须用 ref：普通 Set 的改动不触发响应式更新，点了卡片界面不会变。
const killOpenMap = ref<Record<string, boolean>>({});
function recKey(r: any): string { return String(r.order_number || r.id || ''); }
function killOpen(r: any): boolean { return !!killOpenMap.value[recKey(r)]; }
function toggleKill(r: any): void {
  const k = recKey(r);
  const isOpen = !!killOpenMap.value[k];
  // 手风琴：同时只展开一个 —— 点开新卡，上一个自动收起；再点已展开的则收起
  killOpenMap.value = isOpen ? {} : { [k]: true };
}
// 模式B：点整条卡片展开/收起杀猪原因（手机上点击区域最大，不用精准点小标签）。
// 没有杀猪结论的卡片点了不反应；标签自身点击带 .stop，不会和卡片点击打架。
function onRecordClick(r: any): void {
  if (!(Number(r.kill_flag) > 0)) return;
  toggleKill(r);
}
function killFullText(r: any): string {
  if (r.kill_flag === 2) return `杀猪跳转：落在「${r.kill_desc || ''}」的锚点局`;
  return r.kill_desc || '';
}

function betLabel(r: any): string {
  // 下注那一刻就知道下的是什么（onBet 推送即带 bet_direction），不必等结算
  return r.bet_direction || '';
}

function betColorClass(r: any): string {
  // 未结算：蓝色「已下注·待开奖」态；结算后按盈亏上色
  if (!r.is_finish) return 'result-betting';
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'result-win';
  if (win < 0) return 'result-lose';
  return 'result-tie';
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function winColor(val: number): string {
  if (val > 0) return 'positive';
  if (val < 0) return 'negative';
  return 'zero';
}

// 卡片底色按「玩家输赢」上色：正在下注=琥珀金 / 赢=绿 / 输=红 / 和(退本金)=中性金
function recordBoxClass(r: any): string {
  if (!r.is_finish) return 'res-betting';
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'res-win';
  if (win < 0) return 'res-lose';
  return 'res-tie';
}

// ===== 卡牌解析 =====
const SUIT_MAP: Record<string, { symbol: string; cls: string }> = {
  S: { symbol: '🖤', cls: 'spade' },    // 黑桃 — 黑色心/桃
  H: { symbol: '❤️', cls: 'heart' },    // 红桃 — 红色心
  C: { symbol: '🌸', cls: 'club' },     // 梅花 — 花
  D: { symbol: '🟦', cls: 'diamond' },  // 方片 — 方块
};
const RANK_VALUE: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, '11': 11, '12': 12, '13': 13 };

function parseCards(raw: string | null): { count: number; symbol: string; suit: string }[] {
  if (!raw || raw === '-' || raw === '[]') return [];
  try {
    const arr: string[] = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((c: string) => typeof c === 'string' && c.includes('.'))
      .map((c: string) => {
        const [suit, rank] = c.split('.');
        const info = SUIT_MAP[suit] || { symbol: suit, cls: '' };
        return { count: RANK_VALUE[rank] || 0, symbol: info.symbol, suit: info.cls };
      });
  } catch {
    return [];
  }
}

// 下注/结算推送：服务端已带完整记录，前端直接插入/更新列表，不重新拉取整页
function applyPushedRecord(rec: any) {
  // 只处理当前查看的账号；列表按 bet_time 倒序，新记录只可能落在第 1 页
  if (!rec || !rec.order_number) return;
  if (rec.userName !== userName.value) return;
  if (currentPage.value !== 1) return;
  // “昨天”区间不含今天，推送的新记录不属于该区间，直接丢弃
  if (range.value === 'yesterday') return;

  const no = rec.order_number;
  const idx = records.value.findIndex((r: any) => r && r.order_number === no);
  if (idx >= 0) {
    // 已存在（例如刚打开页面已经刷到这条）→ 原地更新，不新增条目，避免重复
    records.value[idx] = rec;
  } else if (!pushedOrders.has(no)) {
    // 新记录插到最前；超过每页固定条数则移除末尾多余的（推送 100 条也只显示一页）
    records.value = [rec, ...records.value].slice(0, pageSize);
    totalCount.value += 1;                                   // 直播场次
    totalPages.value = Math.max(totalPages.value, Math.ceil(totalCount.value / pageSize));
  }
  pushedOrders.add(no);

  // 结算推送到达：自动展开这条的杀猪原因（手风琴，只展开这一个；下注推送不展开）
  if (rec.is_finish === 1 && Number(rec.kill_flag) > 0) {
    killOpenMap.value = { [recKey(rec)]: true };
  }

  // 直播收入：仅已结算才计入；同一单只累加差额，避免下注→结算两次推送重复计
  if (rec.is_finish === 1) {
    const win = Number(rec.real_win_total_amount) || 0;
    totalNetWin.value += win - (pushedWin.get(no) || 0);
    pushedWin.set(no, win);
  }
  if (rec.nickName) nickName.value = rec.nickName;
}
const onAccountDataPush = (data: any) => applyPushedRecord(data?.record);

// 房间列表变化 → 重新同步面板（房间数决定杀开关是否可点）
const onRoomListUpdate = () => {
  qkLoad().catch(() => {});
};

onMounted(() => {
  loadData();

  // 全局控制面板：拉取杀开关/门槛/房间数（深链进入也能正确显示）
  qkLoad().catch(() => {});
  // 房间列表变化（服务端拉取数据开关切换）时同步面板的房间数
  SignalRClient.Instance.AddEventListener('onRoomListUpdate', onRoomListUpdate);

  // 兜底：深链/刷新进入时首页未加载房间列表，主动拉一次填充 RoomNumber->RoomName 映射
  SignalRClient.Instance.InvokeAsync<{ [key: string]: RoomVO }>("GetRoomList")
    .then((resp) => {
      const list: RoomVO[] = [];
      for (const key in resp) {
        const room = resp[key];
        Object.setPrototypeOf(room, RoomVO.prototype);
        list.push(room);
      }
      setRoomNameMap(list);
    })
    .catch(() => {});
  // 服务端推送的账号状态实时更新（在线/房间/时间）
  SignalRClient.Instance.AddEventListener("onUserStatusUpdate", (data: any) => {
    if (data?.userName !== userName.value) return;
    online.value = !!data.online;
    onlineTime.value = data.onlineTime || '';
    lastOfflineTime.value = data.lastOfflineTime || '';
    room.value = data.room || '';
  });
  // 下注/结算推送：服务端带完整记录，前端直接插入/更新（去重 + 限制每页条数）
  SignalRClient.Instance.AddEventListener("onBet", onAccountDataPush);
  SignalRClient.Instance.AddEventListener("onSettle", onAccountDataPush);
});

onUnmounted(() => {
  SignalRClient.Instance.RemoveEventListener("onUserStatusUpdate");
  SignalRClient.Instance.RemoveEventListener("onBet");
  SignalRClient.Instance.RemoveEventListener("onSettle");
  SignalRClient.Instance.RemoveEventListener("onRoomListUpdate");
});

function fmtTime(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
</script>

<style scoped lang="scss">
.detail-root {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// ===== 顶栏 =====
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.back {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  font-size: 15px;
  color: var(--kp-gold);
  font-weight: 700;
  background: rgba(216, 178, 95, 0.08);
  border: 1px solid rgba(216, 178, 95, 0.20);
  transition: all .15s;
  user-select: none;

  &:hover {
    background: rgba(216, 178, 95, 0.16);
    border-color: rgba(240, 208, 138, 0.45);
    box-shadow: 0 0 18px -8px rgba(240, 208, 138, 0.7);
    color: var(--kp-gold-hi);
  }
}

.account-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fbeecb 0%, #e8c98a 42%, #cfa559 100%);
  color: #241a09;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow:
    0 0 0 1px rgba(255, 240, 205, 0.45),
    0 0 22px -8px rgba(240, 208, 138, 0.65);
}

.name-line {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: .6px;
  color: var(--kp-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nick {
  font-size: 11px;
  color: var(--kp-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-actions {
  display: flex;
  gap: 2px;
  background: rgba(255, 240, 210, 0.035);
  border: 1px solid rgba(216, 178, 95, 0.12);
  border-radius: 9px;
  padding: 3px;
  flex-shrink: 0;
}

.range-btn {
  font-size: 11px;
  color: var(--kp-muted);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover { color: var(--kp-text-2); }

  &.on {
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.26), rgba(168, 124, 57, 0.10));
    color: var(--kp-gold-hi);
    font-weight: 700;
    box-shadow:
      0 0 14px -7px rgba(240, 208, 138, 0.65),
      inset 0 1px 0 rgba(255, 242, 208, 0.14);
  }
}

// ===== 实时状态条 =====
.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  background: rgba(255, 240, 210, 0.015);
  flex-shrink: 0;

  .ss-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .ss-label {
    font-size: 10px;
    color: var(--kp-faint);
    letter-spacing: .8px;
    flex-shrink: 0;
  }

  .ss-val {
    font-size: 12px;
    font-weight: 600;
    color: var(--kp-text-2);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.on { color: var(--kp-jade); }
    &.off { color: var(--kp-muted); }
  }
}

// ===== 统计条（紧凑单行：标签+数值内联）=====
.stats-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 6px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.stat-card {
  display: flex;
  align-items: baseline;
  gap: 6px;

  &.card-win .sc-label { color: var(--kp-jade); }

  &.card-lose .sc-label { color: var(--kp-bad-soft); }
}

.sc-val {
  font-size: 15px;
  font-weight: 800;
  color: var(--kp-gold-hi);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-family: var(--kp-mono);

  &.positive { color: var(--kp-jade); }
  &.negative { color: var(--kp-bad-soft); }
}

.sc-label {
  font-size: 10px;
  color: var(--kp-muted);
  font-weight: 500;
  letter-spacing: .8px;
}

// ===== 内容区（列表 + 分页）=====
.content-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px;
  min-height: 0;
  min-width: 0;
  // 宽屏限宽居中：给 3 列网格留足空间又不拉满全屏；手机（<900px）自动满宽
  width: 100%;
  max-width: 1560px;
  margin: 0 auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.20);
    border-radius: 2px;
  }
}

.loading, .empty {
  text-align: center;
  padding: 80px 0;
  color: var(--kp-faint);
  font-size: 13px;
  letter-spacing: 1px;
}

// ===== 记录网格：手机 1 列；≥900px 两列；≥1400px 三列（卡片内部始终紧凑单列）=====
.record-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  min-width: 0;
}

// ===== 记录卡片 =====
// 内部 flex 列：头部（含展开的杀猪原因）固定高度 → 中部（牌面/等待态）自动撑满 →
// 收益行沉底。同行某张卡展开变高时，兄弟卡片的中部被拉高填充，不会中间空一块。
.record {
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, #211a12, #181208);
  border: 1px solid rgba(216, 178, 95, 0.16);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
  padding: 10px 12px;
  transition: all .2s;
  overflow: hidden;
  min-width: 0;

  &:hover {
    background: linear-gradient(160deg, #292114, #1d160c);
    border-color: rgba(216, 178, 95, 0.30);
  }

  &.unfinished {
    border-color: rgba(216, 178, 95, 0.30);
  }

  // 有杀猪结论的卡片可点开看原因（整条都是点击区域）
  &.clickable { cursor: pointer; }

  // 展开态：边框提亮，和未展开的卡片区分
  &.expanded {
    border-color: rgba(216, 178, 95, 0.55);
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05), 0 0 0 1px rgba(216, 178, 95, 0.18);
  }

  // 玩家输赢着色（跟在 .unfinished 之后，边框色可覆盖它）：
  // 正在下注=琥珀金 / 赢=绿 / 输=红 / 和=中性金，深色底 + 同色系淡边，扫描一眼定位
  &.res-betting {
    background: linear-gradient(160deg, #2f2410, #221a08);
    border-color: rgba(245, 158, 11, 0.42);
    &:hover { background: linear-gradient(160deg, #3b2d13, #2a2009); }
  }

  &.res-win {
    background: linear-gradient(160deg, #14291e, #0e1c14);
    border-color: rgba(126, 201, 164, 0.40);
    &:hover { background: linear-gradient(160deg, #193424, #102418); }
  }

  &.res-lose {
    background: linear-gradient(160deg, #2e1815, #20100e);
    border-color: rgba(205, 106, 83, 0.40);
    &:hover { background: linear-gradient(160deg, #3b1f1a, #28130f); }
  }

  // 和（退本金）：中性金，比「正在下注」的琥珀更暗更淡，避免两者混淆
  &.res-tie {
    background: linear-gradient(160deg, #221d12, #181410);
    border-color: rgba(216, 178, 95, 0.32);
    &:hover { background: linear-gradient(160deg, #2b2517, #1e1913); }
  }
}

// ===== 卡片头部 =====
// 纵向布局：row1（标签行）与杀猪原因小行各占整行，原因太长时自然换行，不会横向挤压
// flex:none：头部高度随内容（展开原因时变高），不参与中部的撑满分配
.rec-top {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  flex: none;
}

.rec-top-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 脉冲金点 - 直播中
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fdf0cd, #d8b25f);
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(240, 208, 138, 0.7);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}

.rec-desk {
  font-size: 13px;
  color: var(--kp-text);
  font-weight: 700;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

// 房间编号（3001-57）：跟在房间号后面的弱化小字，不抢眼
.rec-room-no {
  font-style: normal;
  font-size: 9px;
  font-weight: 500;
  color: var(--kp-faint, #8a7d63);
  margin-left: 5px;
}

.rec-status {
  font-size: 10px;
  color: var(--kp-jade);
  background: rgba(126, 201, 164, 0.12);
  border: 1px solid rgba(126, 201, 164, 0.20);
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;

  &.done {
    color: var(--kp-muted);
    background: rgba(255, 240, 210, 0.05);
    border-color: rgba(216, 178, 95, 0.12);
  }
}

.rec-time {
  font-size: 10px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-round {
  font-size: 10px;
  color: var(--kp-gold);
  font-weight: 600;
  white-space: nowrap;
}

.rec-settle-time {
  font-size: 9px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-result-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 11px;
  border-radius: 6px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  // 加实一点的背景 + 边框，让文字从深色卡片背景里浮出来
  background: rgba(255, 240, 210, 0.10);
  color: var(--kp-text-2);
  border: 1px solid rgba(216, 178, 95, 0.22);

  i {
    font-style: normal;
    color: var(--kp-gold);
    font-weight: 500;
    margin-left: 2px;
  }

  &.result-win {
    color: #bff0d6;
    background: rgba(126, 201, 164, 0.18);
    border-color: rgba(126, 201, 164, 0.40);
  }

  &.result-lose {
    color: #f3b6a6;
    background: rgba(205, 106, 83, 0.18);
    border-color: rgba(205, 106, 83, 0.40);
  }

  &.result-tie {
    color: #f2dcab;
    background: rgba(216, 178, 95, 0.18);
    border-color: rgba(216, 178, 95, 0.42);
  }

  // 已下注·未开奖：蓝色，区别于结算后的赢(绿)/输(红)/平(金)
  &.result-betting {
    color: #bfe0f7;
    background: rgba(96, 165, 250, 0.16);
    border-color: rgba(96, 165, 250, 0.42);
  }
}

// 杀猪结论小标签：杀(红)/自然(绿)/未杀(琥珀)，跟在「已结束」后面；可点开看原因
.rec-kill-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  // ▾ 角标：提示「可点开」；展开时旋转 180°
  .k-arrow {
    font-style: normal;
    font-size: 8px;
    opacity: 0.75;
    transition: transform 0.15s;
  }
  &.open .k-arrow { transform: rotate(180deg); }

  &.kill {
    color: #ffb4a6;
    background: rgba(205, 106, 83, 0.20);
    border: 1px solid rgba(205, 106, 83, 0.45);
  }

  &.natural {
    color: #9fd8b4;
    background: rgba(126, 201, 164, 0.15);
    border: 1px solid rgba(126, 201, 164, 0.40);
  }

  // 未杀：杀按钮已开但本局跳过（条件不符/无锚点/无人下注等），悬停有具体原因
  &.noskill {
    color: #fcd34d;
    background: rgba(245, 158, 11, 0.16);
    border: 1px solid rgba(245, 158, 11, 0.45);
  }
}

// 点按杀猪标签展开的完整杀猪原因小行（手机端没有悬停，靠这个展示；桌面也可点）
.rec-kill-desc {
  margin-top: 5px;
  padding: 4px 7px;
  font-size: 10px;
  line-height: 1.5;
  color: var(--kp-faint, #a89880);
  background: rgba(255, 240, 210, 0.04);
  border-left: 2px solid rgba(216, 178, 95, 0.35);
  border-radius: 0 4px 4px 0;
  word-break: break-all;
}

// 触屏设备（手机/平板）：没有悬停，全靠点按 —— 放大杀猪标签的点击区域
@media (hover: none) and (pointer: coarse) {
  .rec-kill-tag {
    font-size: 11px;
    padding: 6px 9px;
    min-height: 26px;

    .k-arrow { font-size: 9px; }
  }
}

// ===== PK 对战区 =====
.pk-zone {
  position: relative;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.02);
  border: 1px solid rgba(216, 178, 95, 0.10);
  flex: 1 1 auto; // 与其它中部区块一致：撑满卡片中部
  min-height: 96px;
  box-sizing: border-box;
}

.pk-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  flex-wrap: wrap;
}

.pk-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  flex-shrink: 0;
  min-width: 36px;
  text-align: center;

  &.blue {
    color: var(--kp-gold-hi);
    background: rgba(216, 178, 95, 0.12);
    border: 1px solid rgba(216, 178, 95, 0.24);
  }
  &.red {
    color: var(--kp-bad-soft);
    background: rgba(205, 106, 83, 0.12);
    border: 1px solid rgba(205, 106, 83, 0.24);
  }
}

.pk-cards {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-width: 0;
}

.pk-pts {
  margin-left: auto;
  font-size: 16px;
  font-weight: 900;
  color: var(--kp-gold-hi);
  letter-spacing: 1px;
  flex-shrink: 0;
  text-shadow: 0 0 14px rgba(216, 178, 95, 0.35);

  i {
    font-style: normal;
    font-size: 10px;
    color: var(--kp-faint);
    font-weight: 500;
    margin-left: 2px;
  }
}

.pk-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  margin: 3px 0 3px 36px;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: calc(50% - 24px);
    background: rgba(216, 178, 95, 0.10);
  }
  &::before { left: 0; }
  &::after  { right: 0; }

  span {
    font-size: 10px;
    font-weight: 800;
    color: var(--kp-faint);
    letter-spacing: 1px;
    padding: 0 8px;
    z-index: 1;
  }
}


// ===== 礼物标记 =====
.chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  line-height: 1.2;
  letter-spacing: 0.3px;
  border: 1px solid transparent;

  &.spade   { color: #e2b87a; border-color: rgba(226,184,122,0.22); background: rgba(226,184,122,0.08); }
  &.club    { color: #a9e3c6; border-color: rgba(169,227,198,0.20); background: rgba(169,227,198,0.06); }
  &.heart   { color: #f0a094; border-color: rgba(240,160,148,0.22); background: rgba(240,160,148,0.08); }
  &.diamond { color: #a9c6cf; border-color: rgba(169,198,207,0.20); background: rgba(169,198,207,0.06); }

  &.empty {
    color: var(--kp-faint);
    font-weight: 400;
    background: transparent;
    border-color: transparent;
  }
}

// ===== 真实扑克牌对决布局 =====
// 与 .pk-live（无牌面占位）盒模型完全一致：同 padding/gap/最小高度，flex:1 撑满卡片中部。
// 同行某张卡展开变高时，这里被拉高填充，牌面保持垂直居中 —— 不会中间空一块。
.pk-real-showdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.025);
  border: 1px solid rgba(216, 178, 95, 0.12);
  flex: 1 1 auto;
  min-height: 96px;
  box-sizing: border-box;
}

.showdown-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: none;
}

// ===== 无牌面占位区（正在下注 / 无牌记录）=====
// 盒模型与 .pk-real-showdown 完全一致（同 padding/gap/min-height + flex:1），两者高度严格对齐
.pk-live {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.02);
  border: 1px dashed rgba(216, 178, 95, 0.18);
  flex: 1 1 auto;
  min-height: 96px;
  box-sizing: border-box;
}

.live-wait {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
}

.live-wait-txt {
  font-size: 12px;
  font-weight: 600;
  color: var(--kp-faint, #8a7d63);
  letter-spacing: 1px;
  white-space: nowrap;
}

// 背面朝上的牌：斜纹 + 金边，左右两张微微倾角，中间放提示文字
.card-back {
  width: 26px;
  height: 36px;
  border-radius: 4px;
  flex-shrink: 0;
  background:
    repeating-linear-gradient(45deg, rgba(216, 178, 95, 0.12) 0 3px, transparent 3px 6px),
    linear-gradient(145deg, #241c10, #17110a);
  border: 1px solid rgba(216, 178, 95, 0.30);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  transform: rotate(-5deg);

  &:nth-of-type(2) { transform: rotate(-2deg); }
  &:nth-of-type(3) { transform: rotate(2deg); }
  &:nth-of-type(4) { transform: rotate(5deg); }
}

.showdown-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1; // 撑满中部剩余高度，牌面垂直居中（与 .live-wait 一致）
  min-height: 42px;
}

// 对决中心
.showdown-vs {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 8px;
  flex-shrink: 0;

  .vs-score-xian {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-gold-hi);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(216, 178, 95, 0.40);
  }
  .vs-score-zhuang {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-bad-soft);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(205, 106, 83, 0.35);
  }
  .vs-word {
    font-size: 10px;
    font-weight: 700;
    color: rgba(216, 178, 95, 0.30);
    letter-spacing: 0.5px;
    text-transform: lowercase;
    flex-shrink: 0;
  }
}

// ===== 传统扑克牌 =====
.playing-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 42px;
  border-radius: 4px;
  background: linear-gradient(145deg, #f7f2e6, #e9e2d0);
  border: 1px solid rgba(216,178,95,0.45);
  box-shadow:
    0 1px 3px rgba(0,0,0,0.35),
    0 0 12px -6px rgba(216,178,95,0.55),
    inset 0 0 0 1px rgba(255,255,255,0.28);
  flex-shrink: 0;

  .pc-rank {
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
  }

  .pc-suit {
    font-size: 14px;
    line-height: 1;
    margin-top: -1px;
  }

  &.spade, &.club {
    .pc-rank, .pc-suit { color: #1a1a2e; }
  }
  &.heart, &.diamond {
    .pc-rank, .pc-suit { color: #c53030; }
  }

  &.empty {
    background: rgba(255,240,210,0.02);
    border: 1px dashed rgba(216,178,95,0.18);
    box-shadow: none;
    .pc-rank { color: var(--kp-faint); font-weight: 400; }
  }
}

// ===== 收益明细 =====
// flex:none + margin-top:auto：永远沉在卡片最底部（中部区块撑满时自然到底，双保险）
.rec-amounts {
  display: flex;
  justify-content: space-around;
  min-width: 0;
  flex: none;
  margin-top: auto;
}

.am {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

// 仅 PC 宽屏展示的收益项（手机隐藏，保持原来的三项不变）
.am-pc-only { display: none; }

.am-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: var(--kp-gold);
  background: rgba(216, 178, 95, 0.10);
  border: 1px solid rgba(216, 178, 95, 0.18);
  border-radius: 3px;
  flex-shrink: 0;
}

.am-val {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-family: var(--kp-mono);

  &.gold    { color: var(--kp-gold-hi); text-shadow: 0 0 12px rgba(216, 178, 95, 0.30); }
  &.positive { color: var(--kp-jade); }
  &.negative { color: var(--kp-bad-soft); }
  &.zero    { color: var(--kp-muted); }
  &.dim     { color: var(--kp-muted); font-size: 10px; font-weight: 400; }
}

// ===== 底部分页：固定 =====
.pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  border-top: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.pg-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 16px;
  color: var(--kp-gold);
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover {
    background: rgba(216, 178, 95, 0.12);
    color: var(--kp-gold-hi);
  }

  &.off {
    opacity: 0.25;
    pointer-events: none;
  }
}

.pg-num {
  font-size: 12px;
  color: var(--kp-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: .5px;
  min-width: 60px;
  text-align: center;
}

// ===== 悬浮刷新 =====
.float-refresh {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  color: var(--kp-gold);
  background: linear-gradient(180deg, rgba(216, 178, 95, 0.22), rgba(168, 124, 57, 0.10));
  border: 1px solid rgba(216, 178, 95, 0.34);
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  z-index: 10;
  backdrop-filter: blur(6px);
  box-shadow:
    0 6px 20px -10px #000,
    0 0 22px -12px rgba(240, 208, 138, 0.65);

  &:hover {
    color: #fff6e0;
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.34), rgba(168, 124, 57, 0.16));
    border-color: rgba(246, 227, 182, 0.60);
    box-shadow:
      0 6px 24px -10px #000,
      0 0 28px -10px rgba(240, 208, 138, 0.85);
  }

  &:active {
    transform: scale(0.9) rotate(180deg);
  }
}

// ===== PC 宽屏（≥900px）：记录多列排布，卡片保持紧凑单列 =====
// 手机（<900px）完全不受影响：1 列、原布局、三项收益行
@media (min-width: 900px) {
  // 两列；容器再宽（≥1400px）升三列
  .record-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }

  .record { padding: 11px 13px; }

  // 收益行在窄卡里允许换行，避免四项挤爆
  .rec-amounts { flex-wrap: wrap; gap: 5px 12px; }

  // PC 才显示的元素（有效投注）：手机上隐藏，收益行保持原样
  .am-pc-only { display: flex; }

  // 牌面适度放大（窄卡里放不下 38px，取折中）
  .playing-card { width: 34px; height: 48px; }

  // 顶栏区间按钮（今/昨/2d/3d/7d）：放大到和手机触屏版相当，不再“对不上号”
  .top-actions { gap: 3px; padding: 4px; }
  .range-btn { font-size: 13px; padding: 7px 14px; border-radius: 7px; }

  // 卡片头部 chips 同步放大（状态 / 杀猪结论 / 下注角标）
  .rec-status { font-size: 12px; padding: 3px 10px; }
  .rec-kill-tag { font-size: 12px; padding: 5px 10px; }
  .rec-result-chip { font-size: 12px; padding: 4px 10px; }
}

@media (min-width: 1400px) {
  .record-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
</style>
